/**
 * Phase 4.8b — platform-aware file download helper.
 *
 * Background: the existing ``api.downloadFile`` flow does
 *   fetch(url, headers={X-Telegram-Init-Data: …})
 *     → blob
 *     → <a download> click
 * which works on desktop browsers and Telegram Desktop's WebView but
 * fails silently on the Android Telegram in-app WebView — the
 * synthetic anchor click doesn't trigger the system save dialog.
 *
 * The fix: on mobile Telegram platforms (``android``/``ios``), bypass
 * the WebView's download path entirely by calling
 * ``Telegram.WebApp.openLink(url)``, which hands the URL to the
 * device's default browser. Because the device browser can't see
 * the ``X-Telegram-Init-Data`` request header set via fetch, we
 * also encode the init data as a query parameter (server side
 * accepts it as a fallback to the header — see
 * ``api.dependencies.get_current_user``).
 *
 * The desktop / fallback branch is unchanged from
 * ``api.downloadFile`` — fetch + blob + anchor — so existing
 * operators see no behavior change.
 */

/** The Telegram WebApp ``platform`` values we treat as "mobile in-app".
 *
 *  Per the Telegram WebApp spec the possible values are
 *  ``"android"``, ``"ios"``, ``"tdesktop"``, ``"web"``,
 *  ``"macos"``, ``"weba"`` (Web Z), ``"webk"`` (Web K), ``"unigram"``,
 *  ``"unknown"``. Only ``android`` and ``ios`` need the openLink
 *  workaround — every other shell (desktop, web, macOS, future
 *  Unigram) handles ``<a download>`` correctly.
 */
const MOBILE_PLATFORMS = new Set(['android', 'ios']);

/** Minimal shape of the slice of Telegram.WebApp we touch.
 *
 *  Kept narrow so tests can pass a partial mock without satisfying
 *  the full TelegramWebApp interface (the runtime object has dozens
 *  of fields, most unrelated to downloading).
 */
export interface TelegramWebAppMinimal {
  platform?: string;
  initData?: string;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
}

/** Reads ``window.Telegram?.WebApp`` defensively. Returns ``null``
 *  when the page is opened outside Telegram (e.g. plain browser
 *  tab during local development) — callers fall back to the
 *  anchor-click path. */
function getTelegramWebApp(): TelegramWebAppMinimal | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    Telegram?: { WebApp?: TelegramWebAppMinimal };
  };
  return w.Telegram?.WebApp ?? null;
}

/** Phase 4.8b — open the given file URL in a way that works on every
 *  Telegram WebApp platform.
 *
 *  On Android / iOS Telegram: call
 *  ``Telegram.WebApp.openLink(url + "?init_data=…")``. The URL is
 *  handed to the device's default browser, which can issue a normal
 *  HTTP GET and save the response. The query-string ``init_data``
 *  is the same value the desktop path sends as a header — the API
 *  accepts either.
 *
 *  On desktop / web Telegram or a plain browser tab: fetch the
 *  file with the header, build a blob URL, and trigger a synthetic
 *  ``<a download>`` click. Same code path the pre-Phase-4.8
 *  ``api.downloadFile`` used.
 *
 *  ``fileUrl`` must NOT already contain a query string — the helper
 *  appends ``?init_data=…``. The dependency injection of ``tg`` and
 *  ``fetcher`` keeps the helper unit-testable without globals.
 */
export async function downloadFileViaPlatform(
  fileUrl: string,
  initData: string,
  options: {
    tg?: TelegramWebAppMinimal | null;
    fetcher?: typeof fetch;
    documentRef?: Document;
  } = {},
): Promise<void> {
  const tg = options.tg !== undefined ? options.tg : getTelegramWebApp();
  const platform = tg?.platform;
  if (tg && platform && MOBILE_PLATFORMS.has(platform) && tg.openLink) {
    // Mobile path. The device browser can't see fetch headers, so
    // the init data has to ride on the query string. ``try_instant_view``
    // is set to false so Telegram doesn't try to render the file
    // contents inline — we want the browser's download dialog.
    const separator = fileUrl.includes('?') ? '&' : '?';
    const url = `${fileUrl}${separator}init_data=${encodeURIComponent(initData)}`;
    tg.openLink(url, { try_instant_view: false });
    return;
  }

  // Desktop / web / no-Telegram fallback. fetch + blob + anchor —
  // same path as the legacy ``api.downloadFile`` so operators on
  // tdesktop and plain browsers see no change.
  const fetcher = options.fetcher ?? fetch;
  const doc = options.documentRef ?? document;
  const response = await fetcher(fileUrl, {
    headers: { 'X-Telegram-Init-Data': initData },
  });
  if (!response.ok) {
    throw new Error(`download failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  let filename = fileUrl.split('/').pop() ?? 'file';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/i.exec(disposition);
  if (star?.[1]) filename = decodeURIComponent(star[1]);
  else if (plain?.[1]) filename = plain[1];

  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = doc.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    doc.body.appendChild(a);
    a.click();
    doc.body.removeChild(a);
  } finally {
    // Defer revoke so Safari / Telegram in-app browser has time
    // to start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}
